import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { usePostHog } from "@posthog/react";
import { logOut } from "components/navigation/menu-bar";
import {
  IdentityState,
  switchedOrganisationCookieName,
  useIdentity,
  User,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchUserMemberships } from "data/queries-common";
import GeoscapeDeveloperSvg from "icons/hub-logo-signIn.svg?react";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useIntercom } from "react-use-intercom";
import useDarkMode from "use-dark-mode";
import { UserInvitationsOnBoarding } from "./user-invitations-onboarding";
import { UserMembership } from "data/models";

export const OnboardingCompleteSwitch = () => {
  const { value: isDark } = useDarkMode();
  const [identityState, setUserState] = useIdentity();
  const user = identityState as User;

  const [user_, setUserState_] = useState<IdentityState>(user);
  const membershipsQuery = useQuery({
    queryKey: [QueryKeys.userMembership, user.cognitoSub],
    queryFn: () => fetchUserMemberships(true),
  });

  const posthog = usePostHog();

  useEffect(() => {
    if (
      membershipsQuery.isSuccess &&
      membershipsQuery.data &&
      membershipsQuery.data.length == 0
    ) {
      const { cognitoUser, ...safeUser } = user;
      posthog.capture("user_locked_out", safeUser);
    }
  }, [membershipsQuery.isSuccess]);

  const [hasHandledInvitations, setHasHandledInvitations] = useState(false);
  const [_c, setCookie, removeCookie] = useCookies([
    switchedOrganisationCookieName,
  ]);
  const { update, shutdown } = useIntercom();

  return (
    <div className="w-full h-screen relative">
      <div className="flex flex-col items-center h-full pt-16 bg-background overflow-auto">
        <div className="flex justify-center">
          {useLocation().pathname !== "/sign-up" && (
            <GeoscapeDeveloperSvg
              fill={isDark ? "white" : "black"}
              style={{ width: "450px", height: "100px", padding: "0px" }}
            />
          )}
        </div>
        <div className="h-8" />
        <div className="w-[410px]">
          {membershipsQuery.isSuccess &&
            membershipsQuery.data.length !== 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Organisation Invites</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <UserInvitationsOnBoarding
                    userState={user_ as User}
                    setUserState={setUserState_}
                    setHasHandledInvitations={setHasHandledInvitations}
                  >
                    <Alert>
                      <AlertDescription>
                        You must be part of an organisation to use Geoscape Hub.
                        To create a new organisation, contact
                        support@geoscape.com.au.
                      </AlertDescription>
                    </Alert>
                  </UserInvitationsOnBoarding>
                </CardContent>
                <CardFooter className="justify-end">
                  <Button
                    onClick={() => {
                      setUserState((user_) => {
                        const user = user_ as User;
                        return {
                          firstName: user.firstName,
                          lastName: user.lastName,
                          email: user.email,
                          cognitoSub: user.cognitoSub,
                          isAdmin: user.isAdmin,
                          isSales: user.isSales,
                          organisations: user.organisations,
                          cognitoUser: user.cognitoUser,
                          accessibleCount: user.accessibleCount,
                          onboarding: false,
                          hasPendingInvitations: false,
                          onboardingComplete: user.onboardingComplete,
                          phoneNumber: user.phoneNumber,
                          role: user.role,
                          workPhone: user.workPhone,
                          emailVerified: user.emailVerified,
                          phoneNumberVerified: user.phoneNumberVerified,
                          address: user.address,
                        };
                      });
                    }}
                    disabled={!hasHandledInvitations}
                  >
                    Continue
                  </Button>
                </CardFooter>
              </Card>
            )}
          {membershipsQuery.isSuccess &&
            membershipsQuery.data.length == 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Organisation Invites</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <p className="text-sm">Pending organisation invitations</p>
                  <Alert>
                    <AlertDescription>
                      You have no pending organisation invites. To create a new
                      organisation, contact support@geoscape.com.au.
                    </AlertDescription>
                  </Alert>
                </CardContent>
                <CardFooter className="justify-end">
                  <Button
                    onClick={() =>
                      logOut(setUserState, removeCookie, shutdown, posthog)
                    }
                  >
                    Sign Out
                  </Button>
                </CardFooter>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
};
